import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { EventProps as RbcEventProps } from 'react-big-calendar';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { RobotCalendarEvent } from '../RobotSchedule.types';
import { formatDurationShort } from '../../../utils/schedule';
import {
  StyledRobotScheduleEventContent,
  StyledRobotScheduleResizeCursorBadge,
  StyledRobotScheduleResizeLockedPreview,
  StyledRobotScheduleResizeTooltip,
  TOOLTIP_MAX_WIDTH_PX,
} from './RobotScheduleEvent.styles';

// 🚫 badge sits at the bottom-right of the cursor; the tooltip further down to avoid overlap.
const BADGE_OFFSET_X_PX = 5;
const BADGE_OFFSET_Y_PX = 8;
const TOOLTIP_GAP_PX = 26;
const VIEWPORT_MARGIN_PX = 8;

interface RobotScheduleEventContentProps extends RbcEventProps<RobotCalendarEvent> {
  isDisabled?: boolean;
}

export const RobotScheduleEventContent = ({ event, isDisabled }: RobotScheduleEventContentProps): JSX.Element => {
  const { t } = useTranslation();
  const { scheduleEvent } = event.resource;
  // Multi-day events are split into one segment per day, so derive the cleaning/charging
  // portions from the overlap of this segment with the event's cleaning window.
  const segmentStartMs = event.start.getTime();
  const segmentEndMs = event.end.getTime();
  const cleaningEndMs = dayjs(scheduleEvent.endAt).valueOf();
  const cleaningMs = Math.max(0, Math.min(segmentEndMs, cleaningEndMs) - segmentStartMs);
  const chargingMs = Math.max(0, segmentEndMs - Math.max(segmentStartMs, cleaningEndMs));
  const timeLabel = `${dayjs(scheduleEvent.startAt).format('h:mma')} - ${dayjs(scheduleEvent.endAt).format('h:mma')}`;
  const isPast = dayjs(scheduleEvent.endAt).isBefore(dayjs());

  // While resizing, RBC re-renders this component with `event.start`/`event.end` following the
  // cursor, without flagging it as a preview — so we detect the drag by the rendered span
  // growing past the real (cleaning + charging) span, which a normal render always equals.
  const lockedMs = cleaningMs + chargingMs;
  const previewMs = dayjs(event.end).diff(dayjs(event.start));
  const isInvalidResizePreview = previewMs > lockedMs;

  const [cursorPosition, setCursorPosition] = useState<{ x: number; y: number } | null>(null);

  // Track the cursor ourselves during the drag — RBC only re-renders on 15-minute slot
  // boundaries, too coarse for the badge/tooltip to follow the pointer smoothly.
  useEffect(() => {
    if (!isInvalidResizePreview) {
      setCursorPosition(null);
      return undefined;
    }

    const handleMouseMove = (mouseEvent: MouseEvent): void => {
      setCursorPosition({ x: mouseEvent.clientX, y: mouseEvent.clientY });
    };

    document.addEventListener('mousemove', handleMouseMove);
    return (): void => document.removeEventListener('mousemove', handleMouseMove);
  }, [isInvalidResizePreview]);

  if (isInvalidResizePreview) {
    const cleaningFraction = cleaningMs / previewMs;
    const chargingFraction = chargingMs / previewMs;

    const tooltipLeft = cursorPosition
      ? Math.max(
          Math.min(
            cursorPosition.x + VIEWPORT_MARGIN_PX,
            window.innerWidth - TOOLTIP_MAX_WIDTH_PX - VIEWPORT_MARGIN_PX
          ),
          VIEWPORT_MARGIN_PX
        )
      : 0;

    return (
      <StyledRobotScheduleResizeLockedPreview $cleaningFraction={cleaningFraction} $chargingFraction={chargingFraction}>
        <div className="robot-schedule-event__resize-valid">
          <p className="robot-schedule-event__name">{scheduleEvent.name}</p>
          <p className="robot-schedule-event__time-span">{timeLabel}</p>
        </div>
        <div className="robot-schedule-event__resize-invalid" />
        {chargingMs > 0 && <div className="robot-schedule-event__resize-charging" />}
        {cursorPosition &&
          createPortal(
            <>
              <StyledRobotScheduleResizeCursorBadge
                aria-hidden="true"
                style={{ top: cursorPosition.y + BADGE_OFFSET_Y_PX, left: cursorPosition.x + BADGE_OFFSET_X_PX }}
              >
                🚫
              </StyledRobotScheduleResizeCursorBadge>
              <StyledRobotScheduleResizeTooltip
                aria-hidden="true"
                style={{ top: cursorPosition.y + TOOLTIP_GAP_PX, left: tooltipLeft }}
              >
                {t('machineDetails.robotSchedule.resizeLockedTooltip', { duration: formatDurationShort(cleaningMs) })}
              </StyledRobotScheduleResizeTooltip>
            </>,
            document.body
          )}
      </StyledRobotScheduleResizeLockedPreview>
    );
  }

  return (
    <StyledRobotScheduleEventContent
      $cleaningFlex={cleaningMs}
      $chargingFlex={chargingMs}
      $isPast={isPast}
      $isDisabled={isDisabled}
    >
      <div className="robot-schedule-event__cleaning">
        <p className="robot-schedule-event__name">{scheduleEvent.name}</p>
        <p className="robot-schedule-event__time-span">{timeLabel}</p>
      </div>
      {chargingMs > 0 && <div className="robot-schedule-event__charging" />}
    </StyledRobotScheduleEventContent>
  );
};
